resourcePaths = '{"rootPath":"","assetsPath":""}';/*
    Contentful

    Wrapper class for Contentful's delivery endpoints. Retrieve data based on the name of a
    resource. Returns data in JSON format or as an APIModel.

    Arguments:
        config (required) [Object]:
            Contentful configuration object (See _ContentfulConfiguration.js for more information).
        params (optional) [Object]:
            A list of optional configuration parameters:
                url (optional) [String]:
                    The URL to Contentful's delivery API. If not supplied, uses the default
                    settings from configuration.
                space_id (optional) [String]:
                    The space in which content is to be retrieved from.
                accessToken (optional) [String]:
                    The access token required in order to access Contentful's API.
                returnType (optional) [String]:
                    The format in which data will be returned. "JSON" or "APIModel". Defaults
                    to "JSON".
                verboseLevel (optional) [Integer]:
                    An integer in the range of 0 to 2, with 0 being least verbose and 2 being
                    most verbose. Specifies the amount of metadata included with the returned
                    result. Defaults to 2.

*/

function Contentful(config, params) {

  this._reservedKeywords = {
    url: true,
    accessToken: true,
    returnType: true,
    verboseLevel: true,
    resources: true
  };
  this._url = null;
  this._config = {};
  this._returnType = "JSON"; // JSON | APIModel
  this._accessToken = null;
  this._verboseLevel = 2;
  this._defaultParams = {
    space_id: null
  };

  /*
      Sets the Contentful delivery API url. If no URL is supplied, uses the default settings
      from configuration.

      Arguments:
          url [String]:
              The url of Contentful's delivery API. Defaults to settings from configuration.
  */
  this.setAPIUrl = function(url) {
    if (typeof url === "string") {
      this._url = url;
    } else if (url == null && typeof this._config.url === "string") {
      this._url = this._config.url;
    } else {
      throw new TypeError("Contentful.js invalid format for API url.");
    }
  };


  /*
      Sets the access token in order to obtain authorization to Contentful API.

      Arguments:
          accessToken [String]:
              Sets the access token for Contentful.
  */
  this.setAccessToken = function(accessToken) {
    if (typeof accessToken === "string") {
      this._accessToken = accessToken;
    } else if (accessToken == null && typeof this._config.accessToken === "string") {
      this._accessToken = this._config.accessToken;
    } else {
      throw new TypeError("Contentful.js invalid format for access token.");
    }
  };

  /*
      Sets the verbose level for the returned content.

      Arguments:
          verboseLevel [Integer]:
              An integer between 0 to 2 specifying the verbose level, with 0
              being the least verbose and 2 being the most verbose.
  */
  this.setVerboseLevel = function(verboseLevel) {
    verboseLevel = parseInt(verboseLevel);
    if (!isNaN(verboseLevel) && verboseLevel >= 0 && verboseLevel <= 2)
      this._verboseLevel = verboseLevel;
  };

  /*
      Sets default parameters for Contentful.

      Arguments:
          paramObj [Object]:
              An object consisting of parameter key-value pairs.
  */
  this.setDefaultParameters = function(paramObj) {
    if (typeof paramObj === "object") {
      for (var key in paramObj) {
        if (!this._reservedKeywords[key]) {
          if (paramObj.hasOwnProperty(key)) {
            if (typeof paramObj[key] === "string") {
              this._defaultParams[key] = paramObj[key];
            } else if (paramObj[key] === null && typeof this._config[key] === "string") {
              this._defaultParams[key] = this._config[key];
            } else {
              throw new TypeError("Contentful.js invalid format for parameter" + key);
            }
          }
        }
      }
    }
  };

  /*
      Sets the content type to be returned on any resource request.

      Arguments:
          type [String]:
              The format in which the content will be returned. The type must be either
              "JSON" or "APIModel".
  */
  this.setReturnType = function(type) {
    if (type === "JSON" || type === "APIModel") {
      this._returnType = type;
    } else if (type == null && typeof this._config.returnType === "string" &&
        (this._config.returnType === "JSON" || this._config.returnType === "APIModel")) {
      this._returnType = this._config.returnType;
    } else {
      throw new TypeError("Contentful.js invalid content return type.");
    }
  };

  /*
      Gets the current setting for the content type that's returned on any resource request.

      Returns:
          [String]
              The content type, either "JSON" or "APIModel".
  */
  this.getReturnType = function() {
    return this._returnType;
  };


  /*
      Retrieve a Contentful resource. Resources are defined in the Contentful configuration
      object.

      Arguments:
          resourceName [String] (required):
              The name of the resource.
          parameters [Object] (optional):
              A object with key-value pairs of parameter names and values to be sent with the
              resource request.
          callback [function] (optional):
              Callback function when the resource is retrieved.
  */
  this.getResource = function(resourceName, parameters, callback) {
    try {
      resourceName = resourceName.toUpperCase();
      var resourceConfig = this._config.resources[resourceName];
      if (!resourceConfig) {
        throw new ReferenceError("Contentful.js resource name doesn't exist.");
      }
      var ajaxReq = this._constructRequestObject(resourceConfig, parameters);
      ajaxReq.complete = this._onResourceResponse.bind(this, callback || function() {});
      $.ajax(ajaxReq);
    } catch (e) {
      throw e;
    }
  };

  this._setConfiguration = function(config) {
    if (typeof config === "object") {
      this._config = config;
      if (this._config.url)
        this._url = this._config.url;
      if (this._config.accessToken)
        this.setAccessToken(this._config.accessToken);
      if (this._config.returnType)
        this.setReturnType(this._config.returnType);
      if (this._config.verboseLevel)
        this.setVerboseLevel(this._config.verboseLevel);
      this.setDefaultParameters(this._config);
    }
  };

  this._constructRequestObject = function(resourceConfig, parameters) {
    var requestObject = {};
    if (!resourceConfig.url) {
      throw new URIError("Contentful.js missing URL in resource.");
    }

    try {
      requestObject.url = this._applyParametersToUrl(this._url + resourceConfig.url, parameters);
      requestObject.type = "GET";
      requestObject.headers = new Object();
      requestObject.headers.Authorization = "Bearer " + this._accessToken;

      return requestObject;
    } catch (e) {
      throw e;
    }
  };

  this._applyParametersToUrl = function(url, parameters) {
    // Merge parameters
    var params = $.extend({}, this._defaultParams, parameters);
    var newUrl = "";

    var currentVar = "";
    var curlyStack = new Array();
    for (var i = 0; i < url.length; i++) {
      if (url[i] === "{") {
        curlyStack.push("{");
      } else if (url[i] === "}") {
        if (curlyStack.pop() === "{") {
          if (params[currentVar] != null) {
            newUrl = newUrl + params[currentVar];
          } else {
            throw new URIError("Contentful.js missing URL parameter.")
          }
          currentVar = "";
        } else {
          throw new SyntaxError("Contentful.js malformed resource URL.")
        }
      } else if (curlyStack.peek() === "{") {
        currentVar = currentVar + url[i];
      } else {
        newUrl = newUrl + url[i];
      }
    }
    if (curlyStack.length > 0) {
      throw new SyntaxError("Contentful.js malformed resource URL.");
    }
    return newUrl;
  };

  this._onResourceResponse = function(callback, response) {
    if (response.status == 200) {
      if (this._returnType === "JSON") {
        // TODO: implement logic to consume from localStore if _ContentfulCache.enableCache is true
        callback(ContentfulParser.parseToFlatten(response.responseJSON, this._verboseLevel));
      } else if (this._returnType === "APIModel") {
        callback(response.responseJSON, response.status);
      }
    } else {
      this._handleRequestFailure(callback, response);
    }
  };

  this._handleRequestFailure = function(callback, response) {
    console.log("error in getting resource");
    callback(null, response.status);
  };

  this.constructor = function(config, params) {
    this._ch = _ContentfulCache.enableCache ? new ContentfulCacheHelper(config, params) : null;
    this._setConfiguration(config);
    if (params) {
      if (params.accessToken)
        this.setAccessToken(params.accessToken);
      if (params.url)
        this.setAPIUrl(params.url);
      if (params.returnType)
        this.setReturnType(params.returnType);
      if (params.verboseLevel != null)
        this.setVerboseLevel(params.verboseLevel);
      this.setDefaultParameters(params);
    }
  };

  this.constructor(config, params);
}
